// @ts-check

/**
 * @file Renders the user edit screen.
 */

import {Form} from "@kaspernj/api-maker/build/form"
import FontAwesome6 from "@expo/vector-icons/FontAwesome6"
import {useLocalSearchParams, useRouter} from "expo-router"
import {createRef, memo, useEffect} from "react"
import {shapeComponent, ShapeComponent} from "set-state-compare/build/shape-component.js"
import {Pressable, View} from "react-native-propforge"
import _ from "gettext-universal/build/src/translate.js"
import useLocale from "gettext-universal/build/src/use-locale-expo.js"
import Button from "../../components/button"
import buildRoutePath from "../../shared/build-route-path"
import Card from "../../components/card"
import errorMessage from "../../shared/error-message"
import FormTextInput from "../../components/form/form-text-input"
import OrganizationsSelect from "../../components/organizations/select"
import {optionalRouteParam} from "../../shared/route-params"
import {User} from "../../frontend-models/user"
import {useScreenLoading} from "../../components/board-layout/screen-loading"
import useThemeColors, {useThemeName} from "../../shared/use-theme-colors"
import Text from "@kaspernj/api-maker/build/utils/text"

/** @import {NamedExoticComponent, ReactNode} from "react" */
/** @type {Record<string, object>} */
const styles = {}

/** @typedef {{[key: string]: unknown}} UsersEditScreenProps */

/**
 * @typedef {object} UsersEditScreenState
 * @property {boolean} active Whether the edited user is active.
 * @property {boolean} loading Whether user data is loading.
 * @property {boolean} saving Whether the save action is running.
 * @property {string} selectedPrimaryOrganizationId Organization id selected as primary.
 * @property {{kind: "error" | "success", text: string} | null} status Status message shown after load or save failures.
 * @property {User | null} user Existing user model, or null for a new user.
 */

/**
 * User edit screen.
 * @augments {ShapeComponent<UsersEditScreenProps, UsersEditScreenState>}
 */
class UsersEditScreen extends ShapeComponent {
  /** @type {ReturnType<typeof useLocalSearchParams>} */
  params
  /** @type {ReturnType<typeof useRouter>} */
  router
  /** @type {string | null} */
  userID
  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()
  formObjectRef = createRef()
  /** @type {UsersEditScreenState} */
  state = {
    active: true,
    loading: true,
    saving: false,
    selectedPrimaryOrganizationId: "",
    status:
      /** @type {{kind: "error" | "success", text: string} | null} */ null,
    user: /** @type {User | null} */ null
  }

  /**
   * Initializes route state and loads user form data.
   * @returns {void} No return value.
   */
  setup() {
    useLocale()
    useScreenLoading(this.s.loading)
    const params = useLocalSearchParams()
    const router = useRouter()

    const userID = optionalRouteParam(params, "id", "User ID")
    this.params = params
    this.router = router
    this.userID = userID
    this.colors = useThemeColors()
    this.themeName = useThemeName()
    useEffect(() => {
      let cancelled = false
      const loadData = async () => {
        try {
          if (userID) {
            const user = await User.find(userID)
            if (cancelled) return
            this.s.active = Boolean(user.active())
            this.s.loading = false
            this.s.selectedPrimaryOrganizationId = String(
              user.primaryOrganizationId() || ""
            )
            this.s.user = user
          } else {
            if (cancelled) return
            this.s.active = true
            this.s.loading = false
          }
        } catch (error) {
          if (cancelled) return
          this.s.loading = false
          this.s.status = {
            kind: "error",
            text: errorMessage(error)
          }
        }
      }
      void loadData()
      return () => {
        cancelled = true
      }
    }, [userID])
  }

  /**
   * Renders the user form.
   * @returns {ReactNode} User edit screen content.
   */
  render() {
    const {
      active,
      loading,
      saving,
      selectedPrimaryOrganizationId,
      status,
      user
    } = this.s
    const {userID} = this.tt
    if (loading) {
      return (
        <View
          style={styles.loadingScreen ||= {flex: 1}}
          testID="userEditScreen"
        />
      )
    }
    return (
      <View
        style={styles.screen ||= {
          flex: 1
        }}
        testID="userEditScreen"
      >
        <Text
          style={styles[`screenTitle-${this.tt.themeName}`] ||= {
            fontSize: 24,
            fontWeight: 700,
            marginBottom: 16
          }}
          testID="userEditScreen/title"
        >
          {userID ? _("Edit user") : _("New user")}
        </Text>
        <Form formObjectRef={this.formObjectRef} onSubmit={this.tt.onSubmit}>
          <Card
            style={styles.formCardSpacing ||= {
              gap: 12,
              maxWidth: 520,
              width: "100%"
            }}
            testID="userEditScreen/form"
          >
            <FormTextInput
              defaultValue={user ? user.email() || "" : ""}
              label={_("Email")}
              name="email"
              testID="userEditScreen/emailInput"
            />
            <FormTextInput
              defaultValue={user ? user.firstName() || "" : ""}
              label={_("First name")}
              name="firstName"
              testID="userEditScreen/firstNameInput"
            />
            <FormTextInput
              defaultValue={user ? user.lastName() || "" : ""}
              label={_("Last name")}
              name="lastName"
              testID="userEditScreen/lastNameInput"
            />
            <FormTextInput
              defaultValue=""
              label={_("Password")}
              name="password"
              secureTextEntry
              testID="userEditScreen/passwordInput"
            />
            <Pressable
              onPress={this.tt.onActivePress}
              style={styles.activeCheckbox ||= {
                alignItems: "center",
                flexDirection: "row",
                gap: 10
              }}
              testID="userEditScreen/activeCheckbox"
            >
              <View
                style={styles[`activeCheckboxBox-${this.tt.themeName}-${active}`] ||= {
                  alignItems: "center",
                  backgroundColor: active ? this.tt.colors.primary : this.tt.colors.surface,
                  borderColor: active ? this.tt.colors.primary : this.tt.colors.borderStrong,
                  borderRadius: 4,
                  borderWidth: 1,
                  height: 22,
                  justifyContent: "center",
                  width: 22
                }}
                testID="userEditScreen/activeCheckbox/box"
              >
                <View
                  style={styles.activeCheckboxMark ||= {
                    alignItems: "center",
                    justifyContent: "center"
                  }}
                  testID="userEditScreen/activeCheckbox/mark"
                >
                  {active &&
                    <FontAwesome6 color={this.tt.colors.onPrimary} name="check" size={12} />
                  }
                </View>
              </View>
              <Text
                style={styles[`activeCheckboxLabel-${this.tt.themeName}`] ||= {
                  color: this.tt.colors.textMuted,
                  fontWeight: 600
                }}
                testID="userEditScreen/activeCheckbox/label"
              >
                {_("Active")}
              </Text>
            </Pressable>
            <OrganizationsSelect
              blankOptionText={_("No organization")}
              defaultValue={selectedPrimaryOrganizationId}
              label={_("Primary organization")}
              name="primaryOrganizationId"
              testID="userEditScreen/primaryOrganizationInput"
            />
            {status &&
              <Text
                style={styles[`status-${this.tt.themeName}-${status.kind}`] ||= {
                  color: status.kind === "success" ? this.tt.colors.successText : this.tt.colors.dangerText,
                  fontWeight: 600
                }}
                testID="userEditScreen/statusMessage"
              >
                {status.text}
              </Text>
            }
            <Button
              disabled={saving}
              iconName="floppy-disk"
              label={saving ? _("Saving...") : _("Save")}
              onPress={this.tt.onSubmit}
              testID="userEditScreen/saveButton"
            />
          </Card>
        </Form>
      </View>
    )
  }

  /**
   * Saves a new or existing user.
   * @returns {Promise<void>} Resolves after save handling finishes.
   */
  onSubmit = async () => {
    const {params, router, userID} = this.tt
    const {active, user} = this.s
    const formObject = this.formObjectRef.current
    if (!formObject) return
    const values = formObject.asObject()
    const password =
      typeof values.password === "string" ? values.password : ""
    const primaryOrganizationId =
      typeof values.primaryOrganizationId === "string"
        ? values.primaryOrganizationId
        : ""
    const attributes = {
      active,
      email: values.email || "",
      firstName: values.firstName || "",
      lastName: values.lastName || "",
      primaryOrganizationId: primaryOrganizationId || null
    }

    if (password) attributes.password = password

    this.s.saving = true
    this.s.status = null
    try {
      if (user && userID) {
        await user.update(attributes)
        router.push(buildRoutePath(params, `/users/${userID}`))
      } else {
        const created = await User.create(attributes)
        router.push(buildRoutePath(params, `/users/${created.id()}`))
      }
    } catch (error) {
      this.s.saving = false
      this.s.status = {
        kind: "error",
        text: errorMessage(error)
      }
    }
  }

  /**
   * Toggles the active checkbox state.
   * @returns {void} No return value.
   */
  onActivePress = () => {
    this.s.active = !this.s.active
  }
}

/** @type {NamedExoticComponent<UsersEditScreenProps>} */
const UsersEditScreenComponent = memo(shapeComponent(UsersEditScreen))

export default UsersEditScreenComponent
